import { Injectable } from '@angular/core';
import { IAggFunc, IAggFuncParams, RowNode } from 'ag-grid-community';
import * as _ from 'underscore';
import { util } from '../util';

export type AggregateFunctionInput<T> = (input:any[]) => T;

export interface IAggregateObject {
  count?:number;
  value?:any;
  toString?():void;
  toNumber?():void;
}

export interface IAggregateFunction {
  sum:AggregateFunctionInput<number>;
  first:AggregateFunctionInput<string|number>;
  concat:AggregateFunctionInput<string>;
  last:AggregateFunctionInput<string|number>;
  min:AggregateFunctionInput<number>;
  max:AggregateFunctionInput<number>;
  cnt:AggregateFunctionInput<IAggregateObject>;
  avg:AggregateFunctionInput<IAggregateObject>;
  custom?: { [key:number]: { [key:string]: IAggFunc } };
}

@Injectable()
export class ReportAggregationService {
  private aggregateACSLMargin = (params:IAggFuncParams):number => {
    let allTotal:number = 0;
    let allACSLFee:number = 0;
    _.forEach(params.rowNode.allLeafChildren, (leaf:RowNode) => {
      allTotal += leaf.data['Detotal'];
      allACSLFee += leaf.data['Decharg'];
    });

    return allTotal? 100 * allACSLFee / allTotal : undefined;
  };

  private aggregateSum = (input:any[]):number => {
    let result:number = null;
    const length:number = input.length;
    for (let i = 0; i < length; i++) {
      if (_.isNumber(input[i])) {
        if (result === null) {
          result = input[ i ];
        } else {
          result += input[ i ];
        }
      }
    }
    return result;
  };

  private aggregateFirst = (input:any[]):string|number => {
    if (input.length >= 0) {
      return input[0];
    } else {
      return null;
    }
  };

  private aggregateConcat = (input:any[]):string => {
    // remove duplicates
    input = _.chain(input)
      .uniq()
      .value();

    // concatenate values (comma-separated)
    let result:string = '';
    const length:number = input.length;
    for (let i = 0; i < length; i++) {
      result += input[ i ] + (i < length - 1? ', ' : '');
    }
    return result;
  };

  private aggregateLast = (input:any[]):string|number => {
    if (input.length >= 0) {
      return input[ input.length - 1 ];
    } else {
      return null;
    }
  };

  private aggregateMin = (input:any[]):number => {
    let result:number = null;
    const length:number = input.length;
    for (let i = 0; i < length; i++) {
      if (_.isNumber(input[i]) && (result === null || result > input[ i ])) {
        result = input[ i ];
      }
    }
    return result;
  };

  private aggregateMax = (input:any[]):number => {
    let result:number = null;
    const length:number = input.length;
    for (let i = 0; i < length; i++) {
      if (_.isNumber(input[i]) && (result === null || result < input[ i ])) {
        result = input[ i ];
      }
    }
    return result;
  };

  private aggregateCnt = (input:any[]):IAggregateObject => {
    const result:IAggregateObject = {
      value: 0,
      toString: function():string {
        return this.value.toString();
      },
      // used for sorting
      toNumber: function():number {
        return this.value;
      }
    };
    const length:number = input.length;
    const countedValues:number[] = [];

    for (let i = 0; i < length; i++) {
      const isGroupAgg = util.notNullOrUndefinedOrEmpty(input[ i ]) && _.isNumber(input[ i ].value);
      if (isGroupAgg) {
        result.value += input[ i ].value;
      } else {
        if (!_.contains(countedValues, input[ i ])) {
          countedValues.push(input[ i ]);
          result.value++;
        }
      }
    }
    return result;
  };

  private aggregateAvg = (input:any[]):IAggregateObject => {
    // the average will be the sum / count
    let sum:number = 0;
    let count:number = 0;
    const length:number = input.length;
    for (let i = 0; i < length; i++) {
      const currentItem:any = input[ i ];
      const itemIsGroupResult = util.notNullOrUndefinedOrEmpty(currentItem) && _.isNumber( currentItem.value) && _.isNumber(currentItem.count);
      // skip values that are not numbers (ie skip empty values)
      if (_.isNumber(currentItem)) {
        sum += currentItem;
        count++;
        // check if it's a group (ie value is a wrapper object)
      } else if (itemIsGroupResult) {
        // we are aggregating groups, so we take the
        // aggregated values to calculate a weighted average
        sum += currentItem.value * currentItem.count;
        count += currentItem.count;
      }
    }
    // avoid divide by zero error
    let value:number = null;
    if (count !== 0) {
      value = sum / count;
    }
    // the result will be an object. when this cell is rendered, only the avg is shown.
    // however, when this cell is part of another aggregation, the count is also needed
    // to create a weighted average for the next level.
    return {
      count: count,
      value: value,
      // the grid by default uses toString to render values for an object, so this
      // is a trick to get the default cellRenderer to display the avg value
      toString: function():string {
        if (_.isNumber(this.value)) {
          return this.value.toString();
        } else {
          return '';
        }
      },
      // used for sorting
      toNumber: function():number {
        return this.value;
      }
    };
  };

  private aggregateFunctions:IAggregateFunction = {
    sum: this.aggregateSum,
    first: this.aggregateFirst,
    concat: this.aggregateConcat,
    last: this.aggregateLast,
    min: this.aggregateMin,
    max: this.aggregateMax,
    cnt: this.aggregateCnt,
    avg: this.aggregateAvg,
    custom: {
      21: {
        'Margin': this.aggregateACSLMargin
      }
    }
  };

  public get aggregate():IAggregateFunction {
    const aggFunc:IAggregateFunction = _.clone(this.aggregateFunctions);
    delete aggFunc.custom;
    return aggFunc;
  }

  public getCustomAggregationFunction(reportId:number, columnName:string):IAggFunc {
    return this.aggregateFunctions.custom?.[reportId]?.[columnName];
  }
}